export interface LiveQuizConfig {
  version: string;
  tieBreakerQuestionIds: string[];
  questions: Array<{
    id: string;
    prompt: string;
    type: "terpene" | "product";
    answers: Array<{ id: string; label: string; terpene?: string; product?: "calm" | "sleep" | "both"; weight?: number }>;
  }>;
  results: Record<string, {
    id: string;
    name: string;
    title: string;
    sourceLabel: string;
    description: string;
    personalityLines: [string, string, string];
    foundIn: string[];
    sourceImage: string;
    shareCard: string;
  }>;
  products: Record<"calm" | "sleep" | "both", {
    id: "calm" | "sleep" | "both";
    name: string;
    description: string;
    affiliateUrl: string;
    imageView: "calm" | "sleep" | "both";
  }>;
}

const idPattern = /^[a-z][a-z0-9-]{1,39}$/;
const string = (value: unknown, max = 500) => typeof value === "string" && value.trim().length > 0 && value.length <= max;
const mediaUrl = (value: unknown) => typeof value === "string" && value.length <= 500 &&
  (/^https:\/\/[a-z0-9.-]+(?:\/[a-z0-9._~!$&'()*+,;=:@%/?#-]*)?$/i.test(value) || /^\/(?:assets|share)\/[a-z0-9._/-]+$/i.test(value));
const httpsUrl = (value: unknown) => typeof value === "string" && value.length <= 500 &&
  /^https:\/\/[a-z0-9.-]+(?:\/[a-z0-9._~!$&'()*+,;=:@%/?#-]*)?$/i.test(value);

export function validateQuizConfig(value: unknown): LiveQuizConfig {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Quiz settings must be an object.");
  const config = value as LiveQuizConfig;
  if (!/^terpene-personality-v[0-9]+$/.test(config.version ?? "")) throw new Error("Use a version such as terpene-personality-v2.");
  const resultIds = Object.keys(config.results ?? {});
  if (resultIds.length < 2 || resultIds.length > 12) throw new Error("Add between 2 and 12 terpenes.");
  for (const id of resultIds) {
    const result = config.results[id];
    if (!idPattern.test(id) || result?.id !== id || !string(result.name, 80) || !string(result.title, 80) ||
        !string(result.sourceLabel, 160) || !string(result.description, 1200) ||
        !Array.isArray(result.personalityLines) || result.personalityLines.length !== 3 ||
        !result.personalityLines.every((line) => string(line, 240)) ||
        !Array.isArray(result.foundIn) || result.foundIn.length < 1 || result.foundIn.length > 12 ||
        !result.foundIn.every((source) => string(source, 80)) ||
        !mediaUrl(result.sourceImage) || !mediaUrl(result.shareCard)) {
      throw new Error(`Complete the result, image and card for ${id}.`);
    }
  }
  if (!Array.isArray(config.questions) || config.questions.length < 2 || config.questions.length > 12) {
    throw new Error("Add between 2 and 12 questions.");
  }
  const questionIds = new Set<string>();
  const answerIds = new Set<string>();
  let productQuestions = 0;
  for (const question of config.questions) {
    if (!idPattern.test(question.id) || questionIds.has(question.id) || !string(question.prompt, 300) ||
        !["terpene", "product"].includes(question.type) || !Array.isArray(question.answers) ||
        question.answers.length < 2 || question.answers.length > 12) throw new Error("Check question IDs, prompts and answer counts.");
    questionIds.add(question.id);
    if (question.type === "product") productQuestions += 1;
    for (const answer of question.answers) {
      if (!idPattern.test(answer.id) || answerIds.has(answer.id) || !string(answer.label, 240)) {
        throw new Error(`Check the answers for ${question.id}.`);
      }
      answerIds.add(answer.id);
      if (question.type === "terpene") {
        if (!answer.terpene || !resultIds.includes(answer.terpene) || answer.product !== undefined ||
            !Number.isInteger(answer.weight ?? 1) || (answer.weight ?? 1) < 1 || (answer.weight ?? 1) > 5) {
          throw new Error(`Check the terpene mapping for ${answer.id}.`);
        }
      } else if (!["calm", "sleep", "both"].includes(answer.product ?? "") || answer.terpene !== undefined) {
        throw new Error(`Check the product mapping for ${answer.id}.`);
      }
    }
  }
  for (const id of resultIds) {
    if (!config.questions.some((question) => question.type === "terpene" && question.answers.some((answer) => answer.terpene === id))) {
      throw new Error(`Add an answer that leads to ${id}.`);
    }
  }
  if (productQuestions !== 1 || !Array.isArray(config.tieBreakerQuestionIds) ||
      config.tieBreakerQuestionIds.length !== config.questions.length - 1 ||
      new Set(config.tieBreakerQuestionIds).size !== config.tieBreakerQuestionIds.length ||
      config.tieBreakerQuestionIds.some((id) => config.questions.find((question) => question.id === id)?.type !== "terpene")) {
    throw new Error("Add one product question and order every terpene question for tie breaking.");
  }
  for (const id of ["calm", "sleep", "both"] as const) {
    const product = config.products?.[id];
    if (product?.id !== id || !string(product.name, 80) || !string(product.description, 400) ||
        !httpsUrl(product.affiliateUrl) || !["calm", "sleep", "both"].includes(product.imageView)) {
      throw new Error(`Complete the ${id} product match.`);
    }
  }
  return config;
}

export function scoreQuiz(answers: unknown, config: LiveQuizConfig): { terpene: string; product: "calm" | "sleep" | "both" } {
  if (!answers || typeof answers !== "object" || Array.isArray(answers)) throw new Error("Quiz answers must be an object.");
  const values = answers as Record<string, unknown>;
  const totals = Object.fromEntries(Object.keys(config.results).map((id) => [id, 0])) as Record<string, number>;
  const selected = new Map<string, LiveQuizConfig["questions"][number]["answers"][number]>();
  for (const question of config.questions) {
    const answer = question.answers.find((option) => option.id === values[question.id]);
    if (!answer) throw new Error("A complete set of valid quiz answers is required.");
    selected.set(question.id, answer);
    if (question.type === "terpene" && answer.terpene) totals[answer.terpene] = (totals[answer.terpene] ?? 0) + (answer.weight ?? 1);
  }
  const high = Math.max(...Object.values(totals));
  let tied = Object.keys(config.results).filter((id) => totals[id] === high);
  for (const id of config.tieBreakerQuestionIds) {
    const terpene = selected.get(id)?.terpene;
    if (tied.length === 1) break;
    if (terpene && tied.includes(terpene)) tied = [terpene];
  }
  const productQuestion = config.questions.find((question) => question.type === "product");
  const product = productQuestion ? selected.get(productQuestion.id)?.product : undefined;
  if (!tied[0] || !product) throw new Error("A complete set of valid quiz answers is required.");
  return { terpene: tied[0], product };
}
